import { AssignToCombobox } from "@/components/inspection/assign-to-combobox";
import type { AssignToValue } from "@/components/inspection/assign-to-utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import LoadingScreen from "@/components/ui/loading-screen";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { getLicenseDetailsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { findPendingInspectionOnLicense } from "@/lib/inspection-pending";
import {
  type InspectionPortalLicenseItem,
  bulkAssignInspections,
  formatInspectionAddress,
  inviteInspectionUser,
  listInspectionOrgUsers,
  unassignInspection,
} from "@/lib/inspection-portal-api";
import { refreshLicensesAfterLocalChange } from "@/lib/license-details-change-notification";
import {
  formatDateNoTimezone,
  formatDateTime,
  getInspectionStatusDisplay,
  getLicenseStatusLabel,
  getQueryErrorMessage,
} from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserRoundX } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

type InspectionDispatchDetailsSheetProps = {
  item: InspectionPortalLicenseItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function InspectionDispatchDetailsSheet({
  item,
  open,
  onOpenChange,
}: InspectionDispatchDetailsSheetProps) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();
  const [assignee, setAssignee] = useState<AssignToValue | null>(null);
  const getToken = () => getAccessTokenSilently();

  const licenseQuery = useQuery({
    ...getLicenseDetailsOptions({
      path: { license_id: item?.license_id ?? "" },
    }),
    enabled: open && isAuthenticated && item != null,
  });

  const usersQuery = useQuery({
    queryKey: [
      "inspection-org-users",
      item?.registrant_account_id,
      item?.license_id,
    ],
    queryFn: () =>
      listInspectionOrgUsers(
        getToken,
        item?.registrant_account_id ?? undefined,
      ),
    enabled: open && isAuthenticated && item != null,
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["inspection-dispatch-licenses"],
      }),
      queryClient.invalidateQueries({ queryKey: ["inspection-org-users"] }),
      queryClient.invalidateQueries({ queryKey: ["licenseDetails"] }),
      refreshLicensesAfterLocalChange(queryClient, item?.license_id ?? []),
    ]);
    await licenseQuery.refetch();
  };

  const assignMutation = useMutation({
    mutationFn: async () => {
      if (!item || !assignee) throw new Error("Select an assignee");
      if (assignee.type === "invite") {
        return inviteInspectionUser(getToken, {
          email: assignee.email,
          name: assignee.name,
          license_ids: [item.license_id],
        });
      }
      return bulkAssignInspections(getToken, {
        license_ids: [item.license_id],
        assignee_id: assignee.id,
      });
    },
    onSuccess: async () => {
      toast.success(
        assignee?.type === "invite"
          ? "Inspector invited and assigned"
          : "Assignee updated",
      );
      setAssignee(null);
      await refresh();
    },
    onError: (error) => {
      toast.error("Unable to assign inspection", {
        description: getQueryErrorMessage(error),
      });
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (inspectionId: string) =>
      unassignInspection(getToken, inspectionId),
    onSuccess: async () => {
      toast.success("Assignee removed");
      await refresh();
    },
    onError: (error) => {
      toast.error("Unable to remove assignee", {
        description: getQueryErrorMessage(error),
      });
    },
  });

  const license = licenseQuery.data;
  const pendingInspectionId = item?.pending_inspection_id ?? null;
  const inspection = findPendingInspectionOnLicense(
    license?.inspections,
    pendingInspectionId,
  );
  const inspectionDisplay = inspection
    ? getInspectionStatusDisplay(inspection.status)
    : null;
  const isMutating = assignMutation.isPending || unassignMutation.isPending;

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setAssignee(null);
        onOpenChange(nextOpen);
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto px-6 sm:max-w-2xl"
      >
        <SheetHeader>
          <SheetTitle>Inspection Details</SheetTitle>
          <SheetDescription>
            Review the inspection and manage its assignee.
          </SheetDescription>
        </SheetHeader>

        {!item ? null : licenseQuery.isLoading && !license ? (
          <LoadingScreen message="Loading inspection details..." />
        ) : licenseQuery.error ? (
          <p className="mt-6 text-sm text-destructive">
            {getQueryErrorMessage(licenseQuery.error)}
          </p>
        ) : (
          <div className="mt-6 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Inspection</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Detail label="Inspection ID" value={inspection?.id ?? "—"} />
                <div>
                  <p className="text-sm font-medium">Status</p>
                  {inspectionDisplay ? (
                    <Badge variant={inspectionDisplay.variant}>
                      {inspectionDisplay.label}
                    </Badge>
                  ) : (
                    <p className="text-sm text-muted-foreground">—</p>
                  )}
                </div>
                <Detail
                  label="Created"
                  value={
                    inspection?.created_at
                      ? formatDateTime(inspection.created_at)
                      : "—"
                  }
                />
                <Detail
                  label="Inspection date"
                  value={
                    inspection?.effective_date
                      ? formatDateNoTimezone(inspection.effective_date)
                      : "Not completed"
                  }
                />
                <Detail
                  label="Valid until"
                  value={
                    inspection?.effective_end_date
                      ? formatDateNoTimezone(inspection.effective_end_date)
                      : "—"
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Assignees</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {item.assignees?.length ? (
                  <ul className="divide-y rounded-md border">
                    {item.assignees.map((currentAssignee) => (
                      <li
                        key={currentAssignee.id}
                        className="flex items-center justify-between gap-3 p-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {currentAssignee.name?.trim() ||
                              currentAssignee.email}
                          </p>
                          {currentAssignee.name?.trim() ? (
                            <p className="truncate text-xs text-muted-foreground">
                              {currentAssignee.email}
                            </p>
                          ) : null}
                        </div>
                        {pendingInspectionId ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isMutating}
                            onClick={() => {
                              if (
                                confirm(
                                  `Remove ${currentAssignee.name?.trim() || currentAssignee.email} from this inspection?`,
                                )
                              ) {
                                unassignMutation.mutate(pendingInspectionId);
                              }
                            }}
                          >
                            <UserRoundX className="h-4 w-4" />
                            Remove
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No assignee selected.
                  </p>
                )}

                <div className="space-y-2 border-t pt-4">
                  <Label htmlFor="inspection-details-assignee">
                    Add or change assignee
                  </Label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <AssignToCombobox
                      id="inspection-details-assignee"
                      users={usersQuery.data ?? []}
                      value={assignee}
                      onValueChange={setAssignee}
                      disabled={isMutating || usersQuery.isLoading}
                    />
                    <Button
                      type="button"
                      disabled={!assignee || isMutating}
                      onClick={() => assignMutation.mutate()}
                    >
                      {assignMutation.isPending ? "Saving…" : "Assign"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Property</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Detail
                  label="Address"
                  value={
                    license?.property?.display_address ||
                    formatInspectionAddress(item)
                  }
                />
                <Detail label="APN" value={license?.property?.apn || "—"} />
                <Detail
                  label="Jurisdiction"
                  value={
                    license?.jurisdiction?.name || item.jurisdiction_name || "—"
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>License</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Detail
                  label="License ID"
                  value={license?.display_id || item.display_id || "—"}
                />
                <Detail
                  label="License type"
                  value={license?.license_type?.name || "—"}
                />
                <Detail
                  label="Status"
                  value={
                    license?.status
                      ? getLicenseStatusLabel(license.status)
                      : "—"
                  }
                />
                <Detail
                  label="Effective date"
                  value={
                    license?.effective_date
                      ? formatDateNoTimezone(license.effective_date)
                      : "—"
                  }
                />
                <Detail
                  label="Renewal date"
                  value={
                    license?.renewal_date
                      ? formatDateNoTimezone(license.renewal_date)
                      : "—"
                  }
                />
              </CardContent>
            </Card>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm font-medium">{label}</p>
      <p className="text-sm text-muted-foreground">{value}</p>
    </div>
  );
}
